import { useConfigurationStore } from '@stores/configurationStore'
import { useJackpotStore } from '@stores/jackpotStore'
import { formatMoney, getAmountAfterHideCents } from '@utils/money'
import clsx from 'clsx'
import React, { RefObject, useEffect, useMemo, useRef, useState } from 'react'
import style from '../JackpotSection.module.css'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { JackpotSuits, JackpotLinkTypes, JackpotEQLevels } from 'game-ui-types'
import { ICommonJackpotConfig } from './types'

interface JackpotOdometerProps {
    type: JackpotSuits | JackpotLinkTypes | JackpotEQLevels
    config: ICommonJackpotConfig
    realTimeAmount?: number
    amount: number
    isStatic: boolean
    useOdometer: boolean
    containerRef: RefObject<HTMLDivElement | null>
    onRealTimeOdometerUpdate?: (value: number) => void
}

interface StaticDigitsProps {
    amount: number
    digitWidth: number
    spacesStyle: Record<string, string>
    onRealTimeOdometerUpdate?: (value: number) => void
}

interface AnimatedDigitsProps {
    realTimeAmount?: number
    amount: number
    useOdometer: boolean
    digitWidth: number
    spacesStyle: Record<string, string>
    onRealTimeOdometerUpdate?: (value: number) => void
}

interface DigitsColumnProps {
    current: number
    digitWidth: number
    animationDuration?: number
    onAnimationEnd?: () => void
}

/**
 * JackpotOdometer Component
 *
 * This component displays the current jackpot value. Based on the passed props it switches between a statically displayed value
 * or an animated one. If the value should be animated based on the useOdometer prop, the AnimatedDigits component switches
 * between an odometer animation or a counter animation.
 *
 * Value are passed down in cents then the respective animation deals with the formatting and calculations when hideCents is true
 */

const DigitsColumn: React.FC<DigitsColumnProps> = ({ current, digitWidth, animationDuration = 0, onAnimationEnd }) => {
    const isFirstRender = useRef(true)

    const renderColumn = useMemo(() => {
        const previous = current - 1 >= 0 ? current - 1 : 9

        const digits = [...Array(10)].map((_, i) => {
            let newClassName = ''

            // On first render do not animate value
            // When animation duration is 0 we are using a counter animation so the correct value should just be in the middle without further movement
            if (isFirstRender.current || !animationDuration) newClassName = i === current ? style.middle : ''
            else if (i === current) newClassName = style.below2middle
            else if (i === previous) newClassName = style.middle2above

            // Only add onAnimationEnd to the digit that will move from below to middle so that we can continue the iteration
            // when using odometer. Otherwise onAnimationEnd will be undefined to stop any previous animation handled from CSS.
            return (
                <div
                    id={newClassName ? 'set-digit-value' : ''}
                    key={i}
                    className={newClassName}
                    style={{ animationDuration: `${animationDuration}s` }}
                    onAnimationEnd={i === current ? onAnimationEnd : undefined}
                >
                    {i}
                </div>
            )
        })

        isFirstRender.current = false

        return digits
    }, [current])

    return (
        <div id="digits-column" className={clsx('digit', style['digits-column'])} style={{ width: digitWidth }}>
            {renderColumn}
        </div>
    )
}

/**
 * Show a value with either an odometer animation or a fixed position count up
 * while still updating the parent with the visible value if onRealTimeOdometerUpdate passed
 */
const AnimatedDigits: React.FC<AnimatedDigitsProps> = ({
    realTimeAmount,
    amount,
    useOdometer,
    digitWidth,
    spacesStyle,
    onRealTimeOdometerUpdate,
}) => {
    const defaultDuration = useJackpotStore((state) => state.jackpotStatsAnimationDurationSeconds)
    const countdownToZeroDuration = useJackpotStore((state) => state.jackpotStatsCountdownToZeroDurationSeconds)
    const currencyHideCentsOnlyInJackpotBoxes = useJackpotStore((state) => state.currencyHideCentsOnlyInJackpotBoxes)

    const currencyHideCents = useConfigurationStore((state) => state.currencyHideCents)

    const hideCents = currencyHideCentsOnlyInJackpotBoxes || currencyHideCents
    const [targetAmount, setTargetAmount] = useState(amount)
    const currentAmount = useRef(realTimeAmount ?? amount)
    const gsapAnimationAmount = useRef(realTimeAmount ?? amount)
    const [currentFormattedAmount, setCurrentFormattedAmount] = useState(
        formatMoney(realTimeAmount ?? amount, hideCents)
    )
    const [digitChangeDuration, setDigitChangeDuration] = useState(3)

    const getOdometerSpeedSeconds = () => {
        const diff = Math.abs(
            getAmountAfterHideCents(targetAmount, hideCents) - getAmountAfterHideCents(currentAmount.current, hideCents)
        )

        if (diff === 0) return 3

        return Math.min(3, defaultDuration / diff)
    }

    // Use update in both odometer and countup animation to update the current amount and format the visible amount
    // Exit if we have reached the target value
    const update = (nextValue = -1) => {
        if (
            getAmountAfterHideCents(currentAmount.current, hideCents) ===
            getAmountAfterHideCents(targetAmount, hideCents)
        ) {
            // Incase we do hide cents, if visually reached the target without the cents,
            // keep actual value of the target as the final currentAmount
            currentAmount.current = targetAmount

            return
        }

        // No need to update the CSS digit animation duration when counting up,
        // in this case it will rely on the timing of the updates from GSAP
        setDigitChangeDuration(useOdometer ? getOdometerSpeedSeconds() : 0)

        if (nextValue === -1) {
            const isCountingDownwards = currentAmount.current > targetAmount

            // Check direction whether to increment or decrement value
            // Calculate new value to show while still keeping the current amount in cents
            currentAmount.current =
                (getAmountAfterHideCents(currentAmount.current, hideCents) + (isCountingDownwards ? -1 : 1)) *
                (hideCents ? 100 : 1)
        } else {
            currentAmount.current = nextValue
        }

        setCurrentFormattedAmount(formatMoney(currentAmount.current, hideCents))
        onRealTimeOdometerUpdate?.(currentAmount.current)
    }

    useEffect(() => {
        setTargetAmount(amount)
    }, [amount])

    // Whether using odometer or counting up stop GSAP animation if any
    // Start of the animation based on the current amount animating towards the target
    // Exit if target reached
    useGSAP(() => {
        gsap.killTweensOf(gsapAnimationAmount)
        onRealTimeOdometerUpdate?.(currentAmount.current)

        if (
            getAmountAfterHideCents(currentAmount.current, hideCents) ===
            getAmountAfterHideCents(targetAmount, hideCents)
        )
            return

        if (useOdometer) {
            // If using odometer, the update function updates the current amount, and sets the formatted current amount
            // which updates the digits column to set the appropriate classes to animate
            // with every iteration of a complete animation, the onAnimationEnd handler fires to continue with the update until target reached
            update()
        } else {
            // If we are using counter animation, GSAP will take care of timing and onUpdate calls the update function like the odometer css solution,
            // in that case the onAnimationEnd handler is not set since the loop is handled by GSAP and not waiting for the CSS animation to complete
            gsapAnimationAmount.current = currentAmount.current

            gsap.to(gsapAnimationAmount, {
                current: targetAmount,
                ease: 'none',
                onUpdate: () => {
                    const nextValue = Math.trunc(gsapAnimationAmount.current)
                    const tempAmount = getAmountAfterHideCents(nextValue, hideCents)

                    if (tempAmount !== getAmountAfterHideCents(currentAmount.current, hideCents)) update(nextValue)
                },
                duration: amount === 0 ? countdownToZeroDuration : defaultDuration,
            })
        }

        return () => gsap.killTweensOf(gsapAnimationAmount)
    }, [targetAmount, useOdometer])

    return currentFormattedAmount
        .split('')
        .reverse()
        .map((char, i) => {
            if (char === ' ') {
                return (
                    <div key={i} className="space shrink-0" style={spacesStyle}>
                        {char}
                    </div>
                )
            } else if (isNaN(Number(char))) {
                return (
                    <div id={`decimal-place-${i}`} key={i} className="symbol">
                        {char}
                    </div>
                )
            }

            return (
                <DigitsColumn
                    key={i}
                    current={Number(char)}
                    digitWidth={digitWidth}
                    animationDuration={digitChangeDuration}
                    onAnimationEnd={i === 0 && useOdometer ? () => update() : undefined}
                />
            )
        })
}

/**
 * Format and show a static value which will not animate in any way using the same styling as animated digits
 * while still updating the parent with the visible value if onRealTimeOdometerUpdate passed
 */
const StaticDigits: React.FC<StaticDigitsProps> = ({ amount, digitWidth, spacesStyle, onRealTimeOdometerUpdate }) => {
    const currencyHideCentsOnlyInJackpotBoxes = useJackpotStore((state) => state.currencyHideCentsOnlyInJackpotBoxes)
    const currencyHideCents = useConfigurationStore((state) => state.currencyHideCents)
    const hideCents = currencyHideCentsOnlyInJackpotBoxes || currencyHideCents
    const currentAmount = useRef(amount)
    const [currentFormattedAmount, setCurrentFormattedAmount] = useState(formatMoney(amount, hideCents))

    useEffect(() => {
        currentAmount.current = amount
        onRealTimeOdometerUpdate?.(currentAmount.current)
        setCurrentFormattedAmount(formatMoney(amount, hideCents))
    }, [amount])

    return currentFormattedAmount
        .split('')
        .reverse()
        .map((char, i) => {
            if (char === ' ') {
                return (
                    <div key={i} className="space shrink-0" style={spacesStyle}>
                        {char}
                    </div>
                )
            } else if (isNaN(Number(char))) {
                return (
                    <div key={i} className="symbol">
                        {char}
                    </div>
                )
            }

            return (
                <div key={i} style={{ width: digitWidth }}>
                    {Number(char)}
                </div>
            )
        })
}

const JackpotOdometer: React.FC<JackpotOdometerProps> = ({
    type,
    config,
    realTimeAmount,
    amount,
    isStatic,
    useOdometer,
    containerRef,
    onRealTimeOdometerUpdate,
}) => {
    const hideCents = useJackpotStore((state) => state.currencyHideCentsOnlyInJackpotBoxes)
    const viewport = useConfigurationStore((state) => state.viewport)
    const viewportConfig = config[viewport]
    const { odometer: odometerConfig } = viewportConfig.jackpotStatistics

    // Calculated font size to fit starting from config font size as default
    const sizes = useMemo((): {
        fontSize: number
        digitWidth: number
    } => {
        const fontSize = viewportConfig.jackpotStatistics.odometer.fontSize
        // If animating and going to a smaller value use starting point font size to make sure countdown fits
        const characters = formatMoney(
            !isStatic && realTimeAmount && amount < realTimeAmount ? realTimeAmount : amount,
            hideCents
        ).split('')
        const numberOfSpaces = characters.filter((c) => c === ' ').length
        const numberOfDigits = characters.length - numberOfSpaces
        const containerWidth = viewportConfig.jackpotStatistics.amountBox.width

        const adjustFontSize = (newFontSize: number) => {
            const digitWidth = newFontSize / 2 + odometerConfig.digitWidthPadding

            if (numberOfDigits * digitWidth + numberOfSpaces * odometerConfig.spaces.width > containerWidth) {
                return adjustFontSize(newFontSize - 2)
            }

            return { fontSize: newFontSize, digitWidth }
        }

        return adjustFontSize(fontSize)
    }, [amount, viewport, containerRef.current])

    const spacesStyle = {
        width: `${odometerConfig.spaces.width}px`,
    }
    const odometerStyle = {
        fontSize: `${sizes.fontSize}px`,
        lineHeight: `${odometerConfig.lineHeight}px`,
        fontWeight: odometerConfig.fontWeight,
        color: odometerConfig.fontColor,
        ...(odometerConfig.fontColorGradient
            ? {
                  background: odometerConfig.fontColorGradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
              }
            : {}),
        ...(odometerConfig.stroke
            ? {
                  WebkitTextStroke: `${odometerConfig.stroke.width}px ${odometerConfig.stroke.color}`,
                  textStroke: `${odometerConfig.stroke.width}px ${odometerConfig.stroke.color}`,
              }
            : {}),
        ...(odometerConfig.filter
            ? {
                  filter: odometerConfig.filter,
              }
            : {}),
    }

    return (
        amount >= 0 && (
            <div
                id={`odometer-${type.toLowerCase()}`}
                data-value={amount}
                className="relative flex flex-row-reverse justify-center overflow-hidden w-full h-full"
                style={odometerStyle}
            >
                {isStatic ? (
                    <StaticDigits
                        amount={amount}
                        digitWidth={sizes.digitWidth}
                        spacesStyle={spacesStyle}
                        onRealTimeOdometerUpdate={onRealTimeOdometerUpdate}
                    />
                ) : (
                    <AnimatedDigits
                        useOdometer={useOdometer}
                        realTimeAmount={realTimeAmount}
                        amount={amount}
                        digitWidth={sizes.digitWidth}
                        spacesStyle={spacesStyle}
                        onRealTimeOdometerUpdate={onRealTimeOdometerUpdate}
                    />
                )}
            </div>
        )
    )
}

export default JackpotOdometer
